import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, FileText, Dumbbell } from 'lucide-react';
import { TOKENS, limeBtn, displayFont } from '../../lib/designTokens';
import { useTrackCTA } from '../../lib/useTrackCTA';

// CRO Audit §5.3 — single promise, single primary CTA, trust line.
// Replaces the prior multi-CTA hero with ONE conversion-focused funnel entry.
export const HeroRedesign = () => {
  const track = useTrackCTA();
  return (
    <section
      className="relative min-h-[88vh] flex items-center overflow-hidden"
      style={{ backgroundColor: TOKENS.canvas }}
      data-testid="hero-redesign-section"
    >
      {/* Background video, low-opacity */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay muted loop playsInline
          className="w-full h-full object-cover"
          style={{ opacity: 0.18 }}
          poster="https://images.unsplash.com/photo-1700784795176-7ff886439d79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
        >
          <source src="https://customer-assets.emergentagent.com/job_1590f9cf-5e05-4494-9fd1-928104d1fc8f/artifacts/q82791ue_CHICKEN%20DINNER.mp4" type="video/mp4" />
        </video>
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, ${TOKENS.canvas} 0%, rgba(11,11,12,0.85) 60%, rgba(11,11,12,0.55) 100%)` }}
        />
        {/* Subtle lime spotlight */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.04] blur-3xl"
          style={{ backgroundColor: TOKENS.lime }}
        />
      </div>

      <div className="container-ninja relative z-10 pt-24 pb-12">
        <div className="max-w-3xl">
          {/* Eyebrow / category line */}
          <div className="flex items-center gap-2 mb-6 animate-entrance" style={{ animationDelay: '0.05s' }}>
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: TOKENS.lime }}
            />
            <span
              className="text-[11px] uppercase tracking-[0.25em] font-medium"
              style={{ color: TOKENS.lime, ...displayFont }}
              data-testid="hero-eyebrow"
            >
              Evidence-Based · Updated Weekly
            </span>
          </div>

          {/* H1 — One Promise */}
          <h1
            className="text-white text-5xl md:text-6xl lg:text-7xl mb-6 animate-entrance leading-[1.02]"
            style={{ animationDelay: '0.1s', ...displayFont }}
            data-testid="homepage-h1"
          >
            The science of <span style={{ color: TOKENS.lime }}>peptides, performance &amp; longevity</span> — without the noise.
          </h1>

          {/* Subhead — promise restated, concrete */}
          <p
            className="text-zinc-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl animate-entrance"
            style={{ animationDelay: '0.18s' }}
            data-testid="hero-subhead"
          >
            200+ research-backed guides on peptide protocols, HYROX training, recovery and biohacking — written by athletes, grounded in peer-reviewed research. Pick your path below.
          </p>

          {/* SINGLE primary CTA + 1 secondary (audit rule: 1 primary + ≤1 secondary) */}
          <div
            className="flex flex-wrap items-center gap-3 mb-10 animate-entrance"
            style={{ animationDelay: '0.26s' }}
          >
            <Link
              to="/blogs/article/the-complete-peptide-guide"
              className="inline-flex items-center gap-2 px-7 py-4 text-sm md:text-base uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(199,250,62,0.5)]"
              style={limeBtn}
              data-testid="hero-primary-cta"
              onClick={() => track('hero_primary', '/blogs/article/the-complete-peptide-guide')}
            >
              Read the Peptide Guide
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-5 py-4 text-sm uppercase tracking-wider text-zinc-300 hover:text-white transition-colors duration-300"
              style={displayFont}
              data-testid="hero-secondary-cta"
              onClick={() => track('hero_secondary', '/blog')}
            >
              Browse all guides
              <ArrowRight className="w-3.5 h-3.5 opacity-60" />
            </Link>
          </div>

          {/* Trust line directly under primary CTA */}
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-zinc-400 animate-entrance"
            style={{ animationDelay: '0.34s' }}
            data-testid="hero-trustline"
          >
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: TOKENS.lime }} />
              Citation-backed guides
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" style={{ color: TOKENS.lime }} />
              200+ PubMed citations
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Dumbbell className="w-3.5 h-3.5" style={{ color: TOKENS.lime }} />
              Training, recovery & performance guides
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
